import { useState, useEffect, useRef } from 'react';
import client from './api';
import { objectToQueryString } from './utils';

interface AvailabilityOptions {
  variantIds?: number[];
  doordashVariantIds?: number[];
  retailerTypes?: string[];
  zip?: string;
  ignoreInventory?: boolean;
  apiEnv?: string;
  currency?: string;
  hasLocalDelivery?: boolean;
  address?: any;
  collectionIds?: number[];
  brandId?: string;
  thirdPartyIds?: string[];
}

export const useAvailability = ({
  variantIds = [],
  doordashVariantIds = [],
  retailerTypes = ['standard', 'merch_only', 'self_fulfiller'],
  zip,
  ignoreInventory,
  apiEnv,
  currency = 'USD',
  hasLocalDelivery = false,
  address,
  collectionIds = [],
  brandId,
  thirdPartyIds = [],
}: AvailabilityOptions) => {
  const [standardAvailability, setStandardAvailability] =
    useState<Record<string, any>>();
  const [doordashAvailability, setDoordashAvailability] =
    useState<Record<string, any>>();
  const [gopuffAvailability, setGopuffAvailability] =
    useState<Record<string, any>>();
  const [collections, setCollections] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchedCollectionIds = useRef<Record<string, boolean>>({});
  const api = new client(apiEnv);

  const fetchCollections = async (ids: number[]) => {
    if (!ids.length || !brandId) return {};

    try {
      const collectionIdsToFetch = ids.filter(
        id => !fetchedCollectionIds.current[id],
      );

      if (collectionIdsToFetch.length === 0) return collections;

      const collectionsRes = await api.get(
        `/brands/${brandId}/collections?ids=${JSON.stringify(
          collectionIdsToFetch,
        )}`,
      );

      if (collectionsRes.ok) {
        const fetchedCollections = await collectionsRes.json();
        const result = fetchedCollections.reduce(
          (acc: any, collection: any) => {
            acc[collection.id] = collection;
            return acc;
          },
          {},
        );

        // Mark these collection IDs as fetched
        collectionIdsToFetch.forEach(id => {
          fetchedCollectionIds.current[id] = true;
        });

        const updatedCollections = { ...collections, ...result };

        // Fetch availability for collections right after fetching collections
        if (ids.length > 0 && Object.keys(updatedCollections).length > 0) {
          try {
            // Extract all variant IDs from collections
            const collectionVariantIds = [];
            for (const collectionId of ids) {
              const collection = updatedCollections[collectionId];
              if (collection?.listings) {
                for (const listing of collection.listings) {
                  if (listing.variantId) {
                    collectionVariantIds.push(listing.variantId);
                  }
                }
              }
            }

            // Fetch availability for all collection variant IDs
            if (collectionVariantIds.length > 0) {
              const collectionAvailabilityData = await fetchAvailabilityData(
                collectionVariantIds,
                {
                  retailerTypes: [...retailerTypes, 'gopuff'],
                  zip: address?.zip || zip,
                  ignoreInventory,
                },
              );

              // Update collections with availability data
              const collectionsWithAvailability = { ...updatedCollections };
              for (const collectionId of ids) {
                const collection = collectionsWithAvailability[collectionId];
                if (collection?.listings) {
                  // Store availability data on each listing for reference
                  for (const listing of collection.listings) {
                    if (
                      listing.variantId &&
                      collectionAvailabilityData[listing.variantId]
                    ) {
                      const variantAvailability =
                        collectionAvailabilityData[listing.variantId][
                          currency
                        ] || {};
                      listing.availability = variantAvailability;
                    }
                  }

                  // Set collection availability based on all variants
                  const allVariantsAvailable = collection.listings.every(
                    listing => {
                      if (
                        !listing.variantId ||
                        !collectionAvailabilityData[listing.variantId]
                      ) {
                        return false;
                      }
                      const status =
                        collectionAvailabilityData[listing.variantId][currency]
                          ?.status;
                      return ['available', 'preorder'].includes(status);
                    },
                  );
                  collection.isAvailable = allVariantsAvailable;
                }
              }

              // Set collections state once with all updates
              setCollections(collectionsWithAvailability);
              return collectionsWithAvailability;
            }
          } catch (err) {
            console.error('Failed to fetch collection availability', err);
          }
        }

        // If we didn't update collections with availability data, set it here
        setCollections(updatedCollections);
        return updatedCollections;
      }
      return collections;
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to fetch collections'),
      );
      return collections;
    }
  };

  const processAvailabilityData = (availabilityData: Record<string, any>) => {
    return Object.keys(availabilityData).reduce((acc, variantId) => {
      const { status, priceMin, priceMax } =
        availabilityData[variantId][currency] || {};

      acc[variantId] = {
        inventoryStatus: status,
        priceMin,
        priceMax,
      };
      return acc;
    }, {});
  };

  const fetchAvailabilityData = async (ids: number[], options: any = {}) => {
    if (!ids?.length) return {};

    try {
      const chunkSize = 50;
      let availabilityData = {};

      // Chunk ids
      const idChunks = Array.from(
        { length: Math.ceil(ids.length / chunkSize) },
        (_, i) => ids.slice(i * chunkSize, (i + 1) * chunkSize),
      );

      for (let i = 0; i < idChunks.length; i++) {
        const idChunk = idChunks[i];

        const availabilityRes = await api.get(
          `/variants/availability?${objectToQueryString({
            ids: idChunk.length > 0 ? idChunk : undefined,
            ...options,
          })}`,
        );

        if (availabilityRes.ok) {
          const chunkAvailability = (await availabilityRes.json()) || {};
          availabilityData = { ...availabilityData, ...chunkAvailability };
        } else {
          throw new Error('Failed to fetch availability');
        }
      }

      return availabilityData;
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch availability data'),
      );
      return {};
    }
  };

  const fetchAllAvailability = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch standard availability
      if (variantIds.length > 0 || thirdPartyIds.length > 0) {
        const standardAvailabilityData = await fetchAvailabilityData(
          variantIds,
          {
            retailerTypes,
            zip: address?.zip || zip,
            ignoreInventory,
          },
        );

        const processedStandardData = processAvailabilityData(
          standardAvailabilityData,
        );
        setStandardAvailability(processedStandardData);
      }

      // Fetch DoorDash availability
      if (doordashVariantIds.length > 0) {
        const doordashAvailabilityData = await fetchAvailabilityData(
          doordashVariantIds,
          {
            retailerTypes: ['delivery_only'],
            zip: address?.zip || zip,
            ignoreInventory,
          },
        );

        const processedDoordashData = processAvailabilityData(
          doordashAvailabilityData,
        );
        setDoordashAvailability(processedDoordashData);
      }

      // Fetch Gopuff availability if local delivery is enabled and we have an address
      if (
        hasLocalDelivery &&
        address &&
        address.line1?.trim() &&
        address.zip?.trim()
      ) {
        try {
          const gopuffRes = await api.get(
            `/hooks/gopuff/retailer?${objectToQueryString({
              address: `${address.line1.trim() || ''} ${
                address.line2?.trim() || ''
              }`,
              city: address.city,
              state: address.state,
              zip: address.zip,
              country: address.country,
            })}`,
          );

          if (gopuffRes.ok) {
            const gopuffRetailer = await gopuffRes.json();
            const gopuffAvailabilityData = await fetchAvailabilityData(
              variantIds,
              {
                retailerTypes: ['gopuff'],
                ignoreInventory: false,
                retailerIds: [gopuffRetailer.id],
              },
            );

            const processedGopuffData = processAvailabilityData(
              gopuffAvailabilityData,
            );
            setGopuffAvailability(processedGopuffData);
          } else {
            // Clear Gopuff availability if retailer request fails
            setGopuffAvailability({});
          }
        } catch (err) {
          console.error('Failed to fetch Gopuff availability', err);
          // Clear Gopuff availability on error
          setGopuffAvailability({});
        }
      }

      // Collection availability is now fetched right after fetchCollections
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Unknown error occurred'),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const allVariantIds = Array.from(
      new Set([...variantIds, ...thirdPartyIds, ...doordashVariantIds]),
    );

    if (allVariantIds.length > 0 || collectionIds.length > 0) {
      const initFetch = async () => {
        if (collectionIds.length > 0 && brandId) {
          await fetchCollections(collectionIds);
        }
        fetchAllAvailability();
      };

      initFetch();
    }
  }, [
    JSON.stringify(variantIds),
    JSON.stringify(thirdPartyIds),
    JSON.stringify(doordashVariantIds),
    JSON.stringify(retailerTypes),
    JSON.stringify(address),
    JSON.stringify(collectionIds),
    zip,
    hasLocalDelivery,
    brandId,
  ]);

  return {
    standardAvailability,
    doordashAvailability,
    gopuffAvailability,
    collections,
    loading,
    error,
  };
};
